import React, { forwardRef, useCallback, useEffect } from "react";
import PropTypes from "kf-proptypes/widgets";
import { Trans } from "@lingui/macro";
import { Modal as AntModal } from "antd";

import { useTheme } from "base/themes";

import {
  KfIcon,
  KF_ICONS,
  ICON_SIZE,
  ICON_STROKE,
  ICON_PADDING
} from "@widgets/common/components/primitives/kf_icon/index.jsx";
import { Button } from "../../inputs/button/index.jsx";

import "antd/es/modal/style/css";
import styles from "./modal.css";

export const MODAL_TYPE = {
  NORMAL: "nomral",
  FORM: "form"
};

/**
 * Modal Component it renders a modal dialog box.
 */

export function Modal(props) {
  const {
    title,
    titleRenderer,
    children,
    footerRenderer,
    getContainer = () => document.body,
    open = false,
    centered = true,
    closeOnOutsideClick = false,
    closeOnEscape = true,
    destroyOnClose = true,
    closable,
    mask = true,
    style,
    bodyStyle,
    leftActionsRenderer = () => {
      return <div />;
    },
    rightActionsRenderer = () => {
      return (
        <React.Fragment>
          <Button>
            <Trans>Submit</Trans>
          </Button>
        </React.Fragment>
      );
    },
    backgroundClassName,
    className,
    onClose = () => {},
    width,
    height,
    type = MODAL_TYPE.NORMAL,
    maskBackgroundColor = "var(--kf-color-overlay-gray)"
  } = props;

  let { themeClass } = useTheme();

  useEffect(
    function settingTimeOut() {
      setTimeout(() => {
        if (document.querySelector(`.${styles.modal} .ant-modal-content`)) {
          document.querySelector(
            `.${styles.modal} .ant-modal-content`
          ).style.height = height;
        }
      });
    },
    [height]
  );

  const defaultFooterRenderer = useCallback(
    function defaultFooterRender() {
      return (
        <div className={styles.footer}>
          {<div className={styles.leftactions}>{leftActionsRenderer()}</div>}
          <div style={{ flex: "1 1 100%" }} />
          {<div className={styles.rightactions}>{rightActionsRenderer()}</div>}
        </div>
      );
    },
    [leftActionsRenderer, rightActionsRenderer]
  );

  return (
    <AntModal
      title={title ? title : titleRenderer && titleRenderer()}
      footer={footerRenderer ? footerRenderer() : defaultFooterRenderer()}
      getContainer={getContainer}
      visible={open}
      onCancel={onClose}
      destroyOnClose={destroyOnClose}
      centered={centered}
      wrapClassName={`${styles.modal} ${styles[type]} ${backgroundClassName}`}
      className={`${className} ${themeClass}`}
      style={style}
      bodyStyle={bodyStyle}
      maskClosable={closeOnOutsideClick}
      maskStyle={{ backgroundColor: maskBackgroundColor }}
      keyboard={closeOnEscape}
      closeIcon={
        <div className={styles.modalCloseIcon}>
          <KfIcon
            icon={KF_ICONS.CLOSE}
            size={ICON_SIZE.SIZE_16}
            stroke={ICON_STROKE.REGULAR}
            padding={ICON_PADDING.PADDING_4}
          />
        </div>
      }
      mask={mask}
      width={width}
      closable={closable}
    >
      {children}
    </AntModal>
  );
}

export const PlainModal = forwardRef(AntModal);

Modal.propTypes = {
  /**
   * Function that returns the custom title component for the modal.
   */
  titleRenderer: PropTypes.func,
  /**
   * The content to be rendered inside the modal.
   */
  children: PropTypes.node.isRequired,
  /**
   * Function that returns the custom footer component for the modal.
   */
  footerRenderer: PropTypes.func,
  /**
   * Function that returns the custom left actions component for the modal.
   */
  leftActionsRenderer: PropTypes.func,
  /**
   * Function that returns the custom right actions component for the modal.
   */
  rightActionsRenderer: PropTypes.func,
  /**
   * Function that returns the DOM container for the modal.
   */
  getContainer: PropTypes.func,
  /**
   * Specifies whether the modal is open.
   */
  open: PropTypes.bool,
  /**
   * Specifies whether to center the modal vertically and horizontally.
   */
  centered: PropTypes.bool,
  /**
   * Additional styles to apply to the modal.
   */
  // style prop is ex
  style: PropTypes.shape({
    display: PropTypes.string,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }),
  /**
   * Additional styles to apply to the modal body.
   */
  bodyStyle: PropTypes.shape({
    padding: PropTypes.string,
    height: PropTypes.string,
    backgroundColor: PropTypes.string,
    maxHeight: PropTypes.string,
    overflowY: PropTypes.string,
    overflowX: PropTypes.string,
    overflow: PropTypes.string
  }),
  /**
   * Additional class name(s) for the modal background.
   */
  backgroundClassName: PropTypes.string,
  /**
   * Additional class name(s) for the modal.
   */
  className: PropTypes.string,
  /**
   * Specifies whether the modal can be closed.
   */
  closable: PropTypes.bool,
  /**
   * Specifies whether to destroy the modal content when it is closed.
   */
  destroyOnClose: PropTypes.bool,
  /**
   * Specifies whether the modal should be closed when clicking outside of it.
   */
  closeOnOutsideClick: PropTypes.bool,
  /**
   * Specifies whether the modal should be closed when pressing the Escape key.
   */
  closeOnEscape: PropTypes.bool,
  /**
   * Callback function invoked when the modal is closed.
   */
  onClose: PropTypes.func,
  /**
   * The title text for the modal.
   */
  title: PropTypes.string,
  /**
   * The width of the modal.
   */
  width: PropTypes.string,
  /**
   * The height of the modal.
   */
  height: PropTypes.string,
  /**
   * The type of the modal (e.g., "form","normal").
   */
  type: PropTypes.oneOf(Object.values(MODAL_TYPE)),
  /**
   * Specifies whether to show a mask behind the modal.
   */
  mask: PropTypes.bool,
  /**
   * The Background color of the mask
   */
  maskBackgroundColor: PropTypes.string
};

Modal.defaultProps = {
  getContainer: () => document.body,
  open: false,
  centered: true,
  closeOnOutsideClick: false,
  closeOnEscape: true,
  destroyOnClose: true,
  mask: true,
  leftActionsRenderer: () => {
    return <div />;
  },
  rightActionsRenderer: () => {
    return (
      <React.Fragment>
        <Button>
          <Trans>Submit</Trans>
        </Button>
      </React.Fragment>
    );
  },
  onClose: () => {},
  type: MODAL_TYPE.NORMAL
};
